import React, { useEffect } from 'react';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { SpotlightProps } from '@atlassian/jira-spotlight/src/ui/types.tsx';
import { ROADMAP_INTRODUCTION_SPOTLIGHT } from '../../../../controllers/constants.tsx';
import { useOnboardingFlowsActions } from '../../../../controllers/index.tsx';
import {
	useCollectionsIntroductionDismissed,
	useCollectionsIntroductionPrerequisiteDismissalsMet,
} from '../../../../controllers/selectors/collections-hooks.tsx';
import { useProductUsageStartTimestamp } from '../../../../controllers/selectors/product-usage-start-timestamp-hooks.tsx';
import { messages } from './messages.tsx';
import { checkIfOlderThanNDays, useIsLicensedUser } from './utils.tsx';

export const RoadmapIntroductionSpotlight = ({
	collectionsUrl,
	placement,
}: {
	collectionsUrl: string;
	placement?: SpotlightProps['dialogPlacement'];
}) => {
	const { formatMessage } = useIntl();
	const isLicensedUser = useIsLicensedUser();
	const { push } = useRouterActions();
	const expandSideNav = useExpandSideNav();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const isAlreadyInRoadmaps = window?.location.href.includes('roadmap');

	const {
		loadRoadmapIntroductionDismissed,
		loadRoadmapIntroductionPrerequisiteDismissalsMet,
		loadProductUsageStartTimestamp,
		setRoadmapIntroductionDismissed,
	} = useOnboardingFlowsActions();

	useEffect(() => {
		// we can prevent unnecessary calls in these cases
		if (isAlreadyInRoadmaps || !isLicensedUser) {
			return;
		}
		loadRoadmapIntroductionDismissed();
		loadRoadmapIntroductionPrerequisiteDismissalsMet();
		loadProductUsageStartTimestamp();
	}, [
		isAlreadyInRoadmaps,
		isLicensedUser,
		loadProductUsageStartTimestamp,
		loadRoadmapIntroductionDismissed,
		loadRoadmapIntroductionPrerequisiteDismissalsMet,
	]);

	const isIntroductionDismissed = useCollectionsIntroductionDismissed();
	const arePrerequisiteDismissalsMet = useCollectionsIntroductionPrerequisiteDismissalsMet();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();

	if (
		isAlreadyInRoadmaps ||
		!isLicensedUser ||
		isIntroductionDismissed === undefined ||
		isIntroductionDismissed ||
		!arePrerequisiteDismissalsMet ||
		!checkIfOlderThanNDays(productUsageStartTimestamp)
	) {
		return null;
	}

	const dismiss = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'dismiss');
		setRoadmapIntroductionDismissed();
	};

	const openRoadmaps = (analyticsEvent?: UIAnalyticsEvent) => {
		if (analyticsEvent) {
			fireUIAnalytics(analyticsEvent, 'openRoadmaps');
		} else {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'openRoadmaps',
			);
		}
		setRoadmapIntroductionDismissed();
		push(collectionsUrl);
	};

	expandSideNav();

	return (
		<SpotlightTransition>
			<JiraSpotlight
				actions={[
					{
						onClick: (_, analyticsEvent) => dismiss(analyticsEvent),
						text: formatMessage(messages.dismiss),
						testId: 'roadmap-introduction-spotlight-dismiss-button',
					},
					{
						onClick: (_, analyticsEvent) => openRoadmaps(analyticsEvent),
						text: formatMessage(messages.giveItATry),
					},
				]}
				dialogPlacement={placement}
				dialogWidth={275}
				target={ROADMAP_INTRODUCTION_SPOTLIGHT}
				key={ROADMAP_INTRODUCTION_SPOTLIGHT}
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
				targetOnClick={(_event) => openRoadmaps()}
				messageId="polaris-lib-onboarding-flows.ui.collections.tour.roadmap-introduction-spotlight.jira-spotlight"
				messageType="engagement"
			>
				{formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.messageGalaxia
						: messages.message,
				)}
			</JiraSpotlight>
		</SpotlightTransition>
	);
};
